// @ts-expect-error importing untyped JS module into TypeScript
import store from '@/store'

/* Overrides route access in RouterAccessGuard.js for Pro users to enable upsell workflow */
export const isRouteAccessOverridenForProUpselling = (meta: any, itemName: any,) => {
  const upsellRoutesForPro = [
    'discipline', //parent route
    'discipline-infractions',
    'discipline-events',
    'discipline-index',
    'discipline-settings-redirect', // settings redirect route
    'discipline-defaults',
    'discipline-policies',
    'custom-infraction-types',
    'discipline-event-types',
    'forms', //parent route
    'forms-list',
    'health-safety',
    'briefing-type-list',
    'schedule-review',
    'schedule-budgets',
    'schedule-budget-report',
  ]

  const routeName = [itemName, meta?.routeName].find(name => upsellRoutesForPro.includes(name))
  if (!routeName) {
    return false
  }

  // Judged by the features of the route being checked, not the page being viewed: an organization
  // with the feature gets the route's own rules, such as the feature being turned on in settings.
  // Routes no upsell entry names (e.g. forms) are always let through, as before.
  const features = ROUTE_UPSELLING_DETAILS
    .filter(detail => detail.routeName?.includes(routeName))
    .map(detail => detail.feature)
  return features.length === 0 || features.some(feature => !store.state.features.includes(feature))

} 


export const NON_OWNER_UPSELLING_MESSAGE = 'Ask your account owner to unlock these features on the <i>Premium Plan.</i>'

/* Used for overriding upselling details for specific countries or distributors */
export const DETAILS_OVERRIDES: Record<string, Omit<RouteUpSellingDetail, 'feature' | 'title'>> = {
  byxbiOrg: {
    additionalDetails: 'Contact Byxbi Support Team to learn more about this feature.',
    primaryCta: undefined, // Byxbi users should not see the upgrade button as they cannot upgrade themselves
  }
}

/* For a centralized route-specific upselling details */
export type RouteUpSellingDetail = {
  feature: string
  routeName?: string[]
  title: string
  detailsList?: string[]
  detailsParagraph?: string
  additionalDetails?: string
  redirectTo?: Array<{ name: string; params?: Record<string, any> }>
  primaryCta?: {text: string, action: () => void}
  secondaryCtaVideoUrl?: string
  secondaryCtaDocUrl?: string
}

const briefingAndCredentialsConfig = {
  title: 'Keep Your Workforce Informed, Safe, and Certified!',
  detailsList: [
    'Briefings – Share important updates and require team acknowledgments.',
    'Credentials & Documents – Store, verify, and manage employee certifications, personal documents, and Health & Safety forms for compliance.',
  ],
  additionalDetails: 'Simplify HR functions with the <i>Premium Plan.</i>',
  secondaryCtaVideoUrl: 'https://www.youtube.com/embed/Ht8dpqEkW4s?si=e01P4LJ_d3_VP6dX',
}

export const ROUTE_UPSELLING_DETAILS: RouteUpSellingDetail[]= [
  {
    feature: 'bg_geo',
    routeName: [],
    title: 'Unlock Live Location Tracking!',
    detailsList: [
      'Monitor where your team is right now',
      'Review previous worker routes',
      'Improve worker accountability'
    ],
    additionalDetails: 'This feature is available with the <i>Premium Plan.</i>',
    secondaryCtaVideoUrl: 'https://www.youtube.com/embed/S3BmLYHiikA?si=e9D5B60xTRwY3Pxq',
  },
  {
    feature: 'discipline',
    routeName: [
      'discipline',
      'discipline-infractions',
      'discipline-events',
      'discipline-index',
      'discipline-settings-redirect',
      'discipline-defaults',
      'discipline-policies',
      'custom-infraction-types',
      'discipline-event-types'
    ],
    title: 'Unlock Attendance Compliance!',
    detailsParagraph: 'Instantly log and detect policy violations like late clock-ins, early clock-outs, missed shifts and other and attendance patterns.',
    additionalDetails: 'Get better visibility with the <i>Premium Plan.</i>',
    redirectTo: [
      { name: 'discipline-policies', params: { view: 'new' }}
    ],

    secondaryCtaVideoUrl: 'https://www.youtube.com/embed/9W8yc5Td7gU?si=75lPNp4ThzkV48ly'
  },
  {
    feature: 'briefing',
    routeName: ['briefing-type-list'],
    ...briefingAndCredentialsConfig
  },
  {
    feature: 'credential',
    routeName: ['health-safety'],
    ...briefingAndCredentialsConfig,
  },
  {
    feature: 'document',
    routeName: ['health-safety'],
    ...briefingAndCredentialsConfig,
  },
  {
    feature: 'credential_ai',
    routeName: [],
    title: 'Unlock AI-Powered Credential Imports!',
    detailsList: [
      'Upload credential documents in bulk.',
      'Automatically extract credential details with AI.',
      'Review detected details before importing.',
    ],
    additionalDetails: 'Save time managing employee credentials with the <i>Premium Plan.</i>',
  },
  {
    feature: 'form',
    routeName: [],
    title: 'Capture Real-Time Data With Fareclock Forms!',
    detailsList: [
      'Drag-and-drop form builder',
      'Create multiple form templates',
      'Various form field options available',
      'Submit multiple form entries on device',
      'View form entry insights and responses',
      'Export and print form responses',
    ],
    additionalDetails: 'Level up your data collection with the <i>Premium Plan.</i>',
    secondaryCtaVideoUrl: 'https://www.youtube.com/embed/2IdK_Lz4mZE?si=StudMTdI1GCx97of',
  },
  {
    feature: 'form_condition',
    routeName: [],
    title: 'Unlock Conditional Form Fields!',
    detailsList: [
      'Show or hide fields based on earlier answers.',
      'Combine multiple conditions with all/any rules.',
      'Keep forms short by asking only what applies.',
    ],
    additionalDetails: 'Build smarter forms with the <i>Premium Plan.</i>',
  },
  {
    feature: 'form_manager',
    routeName: [],
    title: 'Unlock Manager Form Fields!',
    detailsList: [
      'Add fields that only managers fill in.',
      'Choose which admins can edit each field.',
      'Review and sign off on form entries from the console or mobile app.',
    ],
    additionalDetails: 'Add manager review to your forms with the <i>Premium Plan.</i>',
  },
  {
    feature: 'form_pdf',
    routeName: [],
    title: 'Unlock PDF Document Forms!',
    detailsList: [
      'Attach a PDF and place form answers on it.',
      'Prefill fields from employee profiles.',
      'Protect sensitive answers like ID numbers.',
      'Collect initials and signatures on the document.',
    ],
    additionalDetails: 'Turn forms into completed documents with the <i>Premium Plan.</i>',
  },
  {
    feature: 'schedule_controls',
    routeName: ['schedule-review', 'schedule-budgets', 'schedule-budget-report'],
    title: 'Unlock Schedule Controls!',
    detailsList: [
      'Set daily and weekly hours budgets for each location or group.',
      'See scheduled hours against budget as you build the schedule.',
      'Approve a schedule and lock it against further changes.',
      'Compare budget, scheduled and actual hours in the Labor Budget report.',
    ],
    additionalDetails: 'Keep labor hours on budget with the <i>Premium Plan.</i>',
  }
]

export const handleSecondaryCta = (
  detail: RouteUpSellingDetail,
  openModal?: () => void
) => {
  if (detail.secondaryCtaVideoUrl) {
    openModal?.()
    return
  }

  if (detail.secondaryCtaDocUrl) {
    window.open(detail.secondaryCtaDocUrl, '_blank', 'noopener,noreferrer')
  }
}

/* For creating mock services and endpoints data */
type ApiResponse = {
  id?: number | string
  results: any[]
  items?: any[]
  supplementalData?: any
}

export const dummyClient = {
  get: (endpoint: string): {data: ApiResponse} => {
    const data: ApiResponse = DUMMY_API_DATA[endpoint]
    if (!data) {
      throw new Error(`No dummy data found for endpoint: ${endpoint}`)
    }
    return { data }
  }
}

export const DUMMY_API_DATA: Record<string, any> = {
  'credential/imports': {
    results: [
      {
        id: 9000000000000001,
        sourceFiles: [
          {
            index: 1,
            name: 'employee-certifications.pdf',
            relativePath: 'employee-certifications.pdf',
            size: 2_486_272,
            contentType: 'application/pdf',
          },
        ],
        status: 'ready_for_review',
        progress: 100,
        sourceTotalSize: 2_486_272,
        error: null,
        created: '2026-09-18T01:35:00Z',
      },
      {
        id: 9000000000000002,
        sourceFiles: [
          {
            index: 1,
            name: 'licenses-and-training.zip',
            relativePath: 'licenses-and-training.zip',
            size: 8_912_896,
            contentType: 'application/zip',
          },
        ],
        status: 'processing',
        progress: 64,
        sourceTotalSize: 8_912_896,
        error: null,
        created: '2026-09-17T23:10:00Z',
      },
      {
        id: 9000000000000003,
        sourceFiles: [
          {
            index: 1,
            name: 'first-aid-cards.pdf',
            relativePath: 'first-aid-cards.pdf',
            size: 1_327_104,
            contentType: 'application/pdf',
          },
        ],
        status: 'accepted',
        progress: 100,
        sourceTotalSize: 1_327_104,
        error: null,
        created: '2026-09-17T08:20:00Z',
      },
    ],
    cursor: null,
  },

  // Geo map
  'geo/workers': {
    results: [
      {
        id: 1,
        active: true,
        sortName: "Doe, John",
        firstName: "John",
        middleName: "",
        lastName: "Doe",
        displayName: "JD",
        avatar: null,
        timezone: "America/New_York",
        bgGeoConnectionState: "online",
      },
      {
        id: 2,
        active: true,
        sortName: "Smith, Anna",
        firstName: "Anna",
        middleName: "L",
        lastName: "Smith",
        displayName: "AS",
        avatar: null,
        timezone: "Asia/Singapore",
        bgGeoConnectionState: "offline",
      },
      {
        id: 3,
        active: true,
        sortName: "Garcia, Miguel",
        firstName: "Miguel",
        middleName: "R",
        lastName: "Garcia",
        displayName: "MG",
        avatar: null,
        timezone: "Europe/Madrid",
        bgGeoConnectionState: "online",
      },
      {
        id: 4,
        active: true,
        sortName: "Lee, Sarah",
        firstName: "Sarah",
        middleName: "",
        lastName: "Lee",
        displayName: "SL",
        avatar: null,
        timezone: "Asia/Seoul",
        bgGeoConnectionState: "not_in_use",
      },
      {
        id: 5,
        active: false,
        sortName: "Tan, Kevin",
        firstName: "Kevin",
        middleName: "A",
        lastName: "Tan",
        displayName: "KT",
        avatar: null,
        timezone: "Asia/Manila",
        bgGeoConnectionState: "offline",
      },
      {
        id: 6,
        active: true,
        sortName: "Brown, Emily",
        firstName: "Emily",
        middleName: "",
        lastName: "Brown",
        displayName: "EB",
        avatar: null,
        timezone: "Europe/London",
        bgGeoConnectionState: "online",
      },
      {
        id: 7,
        active: true,
        sortName: "Nguyen, David",
        firstName: "David",
        middleName: "H",
        lastName: "Nguyen",
        displayName: "DN",
        avatar: null,
        timezone: "Australia/Sydney",
        bgGeoConnectionState: "not_in_use",
      },
      {
        id: 8,
        active: false,
        sortName: "Lopez, Maria",
        firstName: "Maria",
        middleName: "",
        lastName: "Lopez",
        displayName: "ML",
        avatar: null,
        timezone: "America/Los_Angeles",
        bgGeoConnectionState: "online",
      },
      {
        id: 9,
        active: true,
        sortName: "Chen, Wei",
        firstName: "Wei",
        middleName: "",
        lastName: "Chen",
        displayName: "WC",
        avatar: null,
        timezone: "Asia/Shanghai",
        bgGeoConnectionState: "not_in_use",
      },
      {
        id: 10,
        active: true,
        sortName: "Patel, Arjun",
        firstName: "Arjun",
        middleName: "",
        lastName: "Patel",
        displayName: "AP",
        avatar: null,
        timezone: "Asia/Kolkata",
        bgGeoConnectionState: "not_in_use",
      },
    ],
    supplementalData: {
      geoLastLocation: {
        1: {
          id: 1,
          latitude: 10.340848826252724,
          longitude: 123.96607729031932,
          timestamp: "2026-01-27T07:10:57Z",
          eventType: "punch",
        },
        2: {
          id: 2,
          latitude: 1.3521,
          longitude: 103.8198,
          timestamp: "2026-01-27T08:10:57Z",
          eventType: "gps",
        },
        3: {
          id: 3,
          latitude: 40.4168,
          longitude: -3.7038,
          timestamp: "2026-01-27T09:10:57Z",
          eventType: "punch",
        },
        4: {
          id: 4,
          latitude: 37.5665,
          longitude: 126.9780,
          timestamp: "2026-01-27T10:10:57Z",
          eventType: "gps",
        },
        5: {
          id: 5,
          latitude: 14.5995,
          longitude: 120.9842,
          timestamp: "2026-01-27T11:10:57Z",
          eventType: "punch",
        },
        6: {
          id: 6,
          latitude: 51.5074,
          longitude: -0.1278,
          timestamp: "2026-01-27T12:10:57Z",
          eventType: "gps",
        },
        7: {
          id: 7,
          latitude: -33.8688,
          longitude: 151.2093,
          timestamp: "2026-01-27T13:10:57Z",
          eventType: "punch",
        },
        8: {
          id: 8,
          latitude: 34.0522,
          longitude: -118.2437,
          timestamp: "2026-01-27T14:10:57Z",
          eventType: "gps",
        },
        9: {
          id: 9,
          latitude: 31.2304,
          longitude: 121.4737,
          timestamp: "2026-01-27T15:10:57Z",
          eventType: "punch",
        },
        10: {
          id: 10,
          latitude: 28.6139,
          longitude: 77.2090,
          timestamp: "2026-01-27T16:10:57Z",
          eventType: "gps",
        },
      },
    },
  },

  // Discipline or attendance compliance
  'discipline/infractions': {
    results: [
      { id: 1, orgUser: 1, dt: "2026-06-20T08:15:10.123Z", points: 10, type: "late", status: "open", note: "always on time" },
      { id: 2, orgUser: 2, dt: "2026-06-19T14:42:33.456Z", points: 5, type: "absent", status: "acknowledged", note: "minor tardiness" },
      { id: 3, orgUser: 3, dt: "2026-06-18T11:05:27.789Z", points: 15, type: "late", status: "open", note: "missed deadline" },
      { id: 4, orgUser: 4, dt: "2026-06-17T09:30:45.210Z", points: 8, type: "missed out", status: "open", note: "late submission" },
      { id: 5, orgUser: 5, dt: "2026-06-16T16:55:12.678Z", points: 12, type: "absent", status: "acknowledged", note: "unapproved absence" },
      { id: 6, orgUser: 6, dt: "2026-06-15T13:22:59.901Z", points: 20, type: "missed out", status: "open", note: "policy violation" },
      { id: 7, orgUser: 7, dt: "2026-06-14T07:48:36.345Z", points: 7, type: "absent", status: "acknowledged", note: "late clock-in" },
      { id: 8, orgUser: 8, dt: "2026-06-13T21:17:08.564Z", points: 3, type: "late", status: "open", note: "minor issue" },
      { id: 9, orgUser: 9, dt: "2026-06-12T10:40:51.777Z", points: 18, type: "absent", status: "open", note: "missed report" },
      { id: 10, orgUser: 10, dt: "2026-06-11T18:29:14.999Z", points: 6, type: "late", status: "acknowledged", note: "incorrect entry" }
    ],

    supplementalData: {
      orgUser: {
        "1": { firstName: "John", lastName: "Doe" },
        "2": { firstName: "Anna", lastName: "Smith" },
        "3": { firstName: "Miguel", lastName: "Garcia" },
        "4": { firstName: "Sarah", lastName: "Lee" },
        "5": { firstName: "Kevin", lastName: "Tan" },
        "6": { firstName: "Emily", lastName: "Brown" },
        "7": { firstName: "David", lastName: "Nguyen" },
        "8": { firstName: "Maria", lastName: "Lopez" },
        "9": { firstName: "Wei", lastName: "Chen" },
        "10": { firstName: "Arjun", lastName: "Patel" },
      },
    },
  },
  'discipline/events': {
    results: [
      { id: 1, orgUser: 1, dt: "2026-06-20T08:15:10.123Z", points: 10, type: 1, status: "open", note: "Repeated unexcused absences despite prior warnings" },
      { id: 2, orgUser: 2, dt: "2026-06-19T14:42:33.456Z", points: 5, type: 2, status: "acknowledged", note: "Arrived late to shift without prior notice" },
      { id: 3, orgUser: 3, dt: "2026-06-18T11:05:27.789Z", points: 15, type: 3, status: "open", note: "Failed to meet project deadline assigned last week" },
      { id: 4, orgUser: 4, dt: "2026-06-17T09:30:45.210Z", points: 8, type: 2, status: "open", note: "Submitted incomplete report past due date" },
      { id: 5, orgUser: 5, dt: "2026-06-16T16:55:12.678Z", points: 12, type: 2, status: "acknowledged", note: "Absent without approved leave or notification" },
      { id: 6, orgUser: 6, dt: "2026-06-15T13:22:59.901Z", points: 20, type: 3, status: "open", note: "Violation of company policy regarding data handling" },
      { id: 7, orgUser: 7, dt: "2026-06-14T07:48:36.345Z", points: 7, type: 1, status: "acknowledged", note: "Multiple late clock-ins recorded this week" },
      { id: 8, orgUser: 8, dt: "2026-06-13T21:17:08.564Z", points: 3, type: 3, status: "open", note: "Minor breach of workplace conduct guidelines" },
      { id: 9, orgUser: 9, dt: "2026-06-12T10:40:51.777Z", points: 18, type: 2, status: "open", note: "Did not submit required report on scheduled date" },
      { id: 10, orgUser: 10, dt: "2026-06-11T18:29:14.999Z", points: 6, type: 1, status: "acknowledged", note: "Incorrect time entry affecting payroll records" }
    ],

    supplementalData: {
      orgUser: {
        "1": { firstName: "John", lastName: "Doe" },
        "2": { firstName: "Anna", lastName: "Smith" },
        "3": { firstName: "Miguel", lastName: "Garcia" },
        "4": { firstName: "Sarah", lastName: "Lee" },
        "5": { firstName: "Kevin", lastName: "Tan" },
        "6": { firstName: "Emily", lastName: "Brown" },
        "7": { firstName: "David", lastName: "Nguyen" },
        "8": { firstName: "Maria", lastName: "Lopez" },
        "9": { firstName: "Wei", lastName: "Chen" },
        "10": { firstName: "Arjun", lastName: "Patel" },
      },
      type: {
        1: {
          "name": "Termination"
        },
        2: {
          "name": "Written Warning"
        },
        3: {
          "name": "Verbal Warning"
        }
      },
    },
  },
  'discipline/custom-infraction-types': {
    results:[
      {
        id: 1,
        name: "Not following code of conduct",
        points: 10,
        active: true
      },
      {
        id: 2,
        name: "Early departure",
        points: 1,
        active: true
      },
      {
        id: 3,
        name: "Harassment",
        points: 10,
        active: true
      },
      {
        id: 4,
        name: "Tardiness",
        points: 1,
        active: true
      },
      {
        id: 5,
        name: "Not wearing uniform",
        points: 1,
        active: true
      },
    ],
  },
  'discipline/event-types': {
    results: [
      {
        id: 1,
        active: true,
        name: "Termination",
      },
      {
        id: 2,
        active: true,
        name: "Written Warning",
      },
      {
        id: 3,
        active: true,
        name: "Verbal Warning",
      },
    ]
  },
  'discipline/policies': {
    results: [
      {
        id: 3398856992672652,
        created: "2024-02-20T05:58:34.614471Z",
        modified: "2025-10-27T23:45:40.560019Z",
        active: true,
        name: "Default",
        pointsExpireAfter: "P3M",
        reminderDurations: [
          {
            type: "before_start",
            duration: 3600,
            notifyAdmin: false
          },
          {
            type: "start",
            duration: 0,
            notifyAdmin: false,
            customMessageTitle: "You must Clock IN now!",
            customMessageBody: "You are required to Clock IN now if you want to get paid!"
          }
        ],
        infractionRules: [
          {
            type: "absent",
            duration: null,
            points: 3
          },
          {
            type: "late",
            duration: 900,
            points: 4
          },
          {
            type: "leave_early",
            duration: 900,
            points: 3
          },
          {
            type: "short_break",
            duration: 900,
            points: 3
          },
          {
            type: "long_break",
            duration: 3600,
            points: 2
          },
          {
            type: "missed_break",
            duration: null,
            points: 3
          },
          {
            type: "missed_out",
            duration: null,
            points: 3
          }
        ],
        pointThresholds: [
          {
            type: 1,
            points: 2
          },
          {
            type: 2,
            points: 4
          },
          {
            type: 3,
            points: 6
          },
          {
            type: 4,
            points: 8
          }
        ],
      }
    ]
  },

  // Briefings and credentialing
  'credential/policies': {
    results: [
      {
        id: 1,
        name: "Default",
        active: true,
        credentialTypes: [ 1 ],
      },
      {
        id: 2,
        name: "Primary",
        active: true,
        credentialTypes: [ 3, 4 ],
      },
      {
        id: 3,
        name: "Secondary",
        active: true,
        credentialTypes: [ 1, 2, 3, 4 ],
      }
    ]
  },
  'credential/types': {
    results: [
      {
        id: 1,
        name: "License",
        active: true,
        documentRequired: true
      },
      {
        id: 2,
        name: "Passport",
        active: true,
        documentRequired: true
      },
      {
        id: 3,
        name: "Signed Waiver",
        active: true,
        documentRequired: true
      },
      {
        id: 4,
        name: "Medical Card",
        active: true,
        documentRequired: true
      }
    ]
  },
  'briefing/types': {
    results: [
      {
        id: 1,
        name: "Biometric consent",
        active: true,
      },
      {
        id: 2,
        name: "Employee Handbook",
        active: true,
      },
      {
        id: 3,
        name: "Security Plan",
        active: true,
      },
      {
        id: 4,
        name: "Training Plan",
        active: true,
      }
    ]
  },

  'employees/{workerId}/credentials': {
    results: [
      {
        id: 3321547199148361,
        credentialType: 2,
        needsApproval: true,
        created: "2024-01-02T19:56:33.875771Z",
        modified: "2024-01-09T18:23:00.152745Z",
        name: "Passport",
        isValid: true,
        hasDocument: true,
      },
      {
        id: 3683089157282488,
        credentialType: 3,
        needsApproval: true,
        created: "2024-01-02T19:56:33.875771Z",
        modified: "2024-01-09T18:23:00.152745Z",
        name: "Signed Waiver",
        isValid: true,
        hasDocument: true,
      },
      {
        id: 1949276302248742,
        credentialType: 4,
        needsApproval: true,
        created: "2024-01-02T19:56:33.875771Z",
        modified: "2024-01-09T18:23:00.152745Z",
        name: "Medical Card",
        isValid: true,
        hasDocument: true,
      },
    ]
  },
  'employees/{workerId}/documents?attachToType=CREDENTIAL': {
    results: [
      {
        id: 1,
        attachToId: 1949276302248742, //usercredential id
        created: "2024-01-03T19:02:47.646800Z",
        modified: "2024-01-03T19:02:47.646840Z",
        name: "test.pdf",
        mimeType: "application/pdf",
        suffix: "pdf",
        size: 12406,
        attachToType: "CREDENTIAL",
        active: true,
      },
    ]
  },
  'employees/{workerId}/documents?attachToType=USER': {
    results: [
      {
        id: 1,
        created: "2021-08-30T12:43:55.713373Z",
        modified: "2021-08-30T12:43:55.713487Z",
        name: "User Document 1",
        mimeType: "text/csv",
        suffix: "csv",
        size: 11560,
        attachToType: "USER",
        attachToId: null,
        active: true,
      },
      {
        id: 2,
        created: "2021-08-30T12:43:55.713373Z",
        modified: "2021-08-30T12:43:55.713487Z",
        name: "User Document 2",
        mimeType: "text/csv",
        suffix: "csv",
        size: 11560,
        attachToType: "USER",
        attachToId: null,
        active: true,
      },
        {
        id: 3,
        created: "2021-08-30T12:43:55.713373Z",
        modified: "2021-08-30T12:43:55.713487Z",
        name: "User Document 3",
        mimeType: "text/csv",
        suffix: "csv",
        size: 11560,
        attachToType: "USER",
        attachToId: null,
        active: true,
      }
    ],
  },
  'briefing/users?worker={workerId}': {
    results: [
      {
        id: 1,
        created: "2026-04-10T05:17:42.848920Z",
        modified: "2026-04-10T05:17:42.848947Z",
        dt: "2026-04-10T05:17:42.831020Z",
        rebriefDt: null,
        briefing: 1
      },
      {
        id: 2,
        created: "2026-04-11T06:20:15.123456Z",
        modified: "2026-04-11T06:20:15.123456Z",
        dt: "2026-04-11T06:20:15.123456Z",
        rebriefDt: null,
        briefing: 2
      },
      {
        id: 3,
        created: "2026-04-12T07:25:30.654321Z",
        modified: "2026-04-12T07:25:30.654321Z",
        dt: "2026-04-12T07:25:30.654321Z",
        rebriefDt: null,
        briefing: 3
      },
      {
        id: 4,
        created: "2026-04-13T08:30:45.987654Z",
        modified: "2026-04-13T08:30:45.987654Z",
        dt: "2026-04-13T08:30:45.987654Z",
        rebriefDt: null,
        briefing: 4
      }
    ],
    supplementalData: {
      briefing: {
        1: { name: "Biometric consent" },
        2: { name: "Employee Handbook" },
        3: { name: "Security Plan" },
        4: { name: "Training Plan" },
      }
    }
  },

  'forms':{
    items: [
      {
        id: 1,
        name: "New Worker Onboarding",
        createdAt: "2025-10-23T13:17:31.671Z",
        updatedAt: "2026-04-16T03:58:20.553Z",
        counts: {
          inProgress: 1,
          done: 1
        }
      },
      {
        id: 2,
        name: "Safety Training and Equipment Guide",
        createdAt: "2025-10-23T13:17:31.671Z",
        updatedAt: "2026-04-16T03:58:20.553Z",
        counts: {
          inProgress: 2,
          done: 1
        }
      },
      {
        id: 3,
        name: "Equipment and Inventory Management",
        createdAt: "2025-10-23T13:17:31.671Z",
        updatedAt: "2026-04-16T03:58:20.553Z",
        counts: {
          inProgress: 1,
          done: 2
        }
      }
    ]
  },

  'forms/{formId}': {
    results:[
      {
        id: 1,
        name: "New Worker Onboarding",
        fields: [
          {
            name: "Full Name",
            label: "Full Name",
            id: "269a22f1-c62d-47d9-b7b0-784748cd4619",
            type: "text",
          },
          {
            name: "Job Title",
            label: "Job Title",
            id: "c7d8f61d-2018-4d07-896f-0e4162868547",
            type: "text",
          },
        ]
      }
    ]
  },

  'forms/{formId}/submissions': {
    items: [
      {
        id: 6543692121742527,
        answers: {},
        submittedBy: 6471379092226270,
        createdAt: "2025-12-16T06:03:02.149Z",
      },
      {
        id: 4941171104128191,
        answers: {},
        submittedBy: 4549604834148352,
        createdAt: "2025-12-04T10:20:43.282Z",
      },
      {
        id: 5724223858940799,
        answers: {
          "269a22f1-c62d-47d9-b7b0-784748cd4619": {
            value: "Chris Johnson",
            location: {
              lat: 14.5372,
              lng: 121.01776
            },
          },
          "c7d8f61d-2018-4d07-896f-0e4162868547": {
            value: "Marketing Manager",
            location: {
              lat: 14.5372,
              lng: 121.01776
            },
          }
        },
        submittedBy: 4171928687614220,
        createdAt: "2025-12-04T00:30:54.502Z",
      },
      {
        id: 5984582226287487,
        answers: {
          "269a22f1-c62d-47d9-b7b0-784748cd4619": {
            value: "Ana Lopez",
            location: {
              lat: 37.38821,
              lng: -121.88908
            },
          },
          "c7d8f61d-2018-4d07-896f-0e4162868547": {
            value: "Marketing Associate",
            location: {
              lat: 37.38821,
              lng: -121.88908
            },
          },
        },
        submittedBy: 6471379092226270,
        createdAt: "2025-11-14T02:47:33.455Z",
      },
    ],
    supplementalData: {
      orgUser: {
        4171928687614220: {
          id: 4171928687614220,
          firstName: "Kevin",
          lastName: "Tan",
          middleName: null,
          displayName: null,
          payrollId: null
        },
        4549604834148352: {
          id: 4549604834148352,
          firstName: "John",
          lastName: "Smith",
          middleName: null,
          displayName: null,
          payrollId: "AGUR"
        },
        6471379092226270: {
          id: 6471379092226270,
          firstName: "Jane",
          lastName: "Doe",
          middleName: "",
          displayName: "Jane Doe",
          payrollId: "1234000"
        }
      },
    }
  },

  // Insights and responses for full name
  'forms/{formId}/fields/269a22f1-c62d-47d9-b7b0-784748cd4619/answers/stats': {
    stats: {
      formId: 5601720788458367,
      fieldId: "269a22f1-c62d-47d9-b7b0-784748cd4619",
      count: 2
    },
  },

  'forms/{formId}/fields/269a22f1-c62d-47d9-b7b0-784748cd4619/answers': {
    items: [
      {
        id: "5724223858940799_269a22f1-c62d-47d9-b7b0-784748cd4619",
        value: "Chris Johnson",
        updatedAt: "2025-12-04T00:36:31.001Z",
        submittedBy: 4171928687614220,
      },
      {
        id: "5984582226287487_269a22f1-c62d-47d9-b7b0-784748cd4619",
        value: "Ana Lopez",
        updatedAt: "2025-11-14T02:48:59.473Z",
        submittedBy: 6471379092226270,
      },
    ],
    supplementalData: {
      orgUser: {
        4171928687614220: {
          id: 4171928687614220,
          firstName: "Kevin",
          lastName: "Tan",
          middleName: null,
          displayName: null,
        },
        6471379092226270: {
          id: 6471379092226270,
          firstName: "Jane",
          lastName: "Doe",
          middleName: "",
          displayName: "Jane Doe",
        }
      }
    }
  },

  // Insights and responses for job title
  'forms/{formId}/fields/c7d8f61d-2018-4d07-896f-0e4162868547/answers/stats': {
    stats: {
      formId: 5601720788458367,
      fieldId: "c7d8f61d-2018-4d07-896f-0e4162868547",
      count: 2
    },
  },
  'forms/{formId}/fields/c7d8f61d-2018-4d07-896f-0e4162868547/answers': {
    items: [
      {
        id: "5724223858940799_c7d8f61d-2018-4d07-896f-0e4162868547",
        value: "Marketing Manager",
        updatedAt: "2025-12-04T00:37:29.785Z",
        submittedBy: 4171928687614220,
      },
      {
        id: "5984582226287487_c7d8f61d-2018-4d07-896f-0e4162868547",
        value: "Marketing Associate",
        updatedAt: "2025-11-14T02:48:59.473Z",
        submittedBy: 6471379092226270,
      },
    ],
    supplementalData: {
      orgUser: {
        4171928687614220: {
          id: 4171928687614220,
          firstName: "Kevin",
          lastName: "Tan",
          middleName: null,
          displayName: null,
        },
        6471379092226270: {
          id: 6471379092226270,
          firstName: "Jane",
          lastName: "Doe",
          middleName: "",
          displayName: "Jane Doe",
        }
      }
    }
  }

}

export const createDummyCrudService = (resource: string) => {
  return {
    list(): Promise<any> {
      const response = dummyClient.get(resource)
      return Promise.resolve(response.data)
    }
  }
}
